/**
 * Narrowing a list of campaigns, wherever it is shown.
 *
 * This lived inside the all-campaigns screen and a channel's own screen had
 * nothing: the Email page listed every email campaign ever written, oldest
 * somewhere in the middle, with no way to ask for the ones still in draft.
 * Two filter bars would have been two sets of rules about what "Sent" means,
 * so there is one, and the pages differ only in what they hide.
 *
 * The status chips carry their own counts, because the useful question is
 * usually "how many are still drafts" rather than "show me the drafts" — and a
 * chip with nothing behind it is disabled rather than hidden, so the row does
 * not reshuffle every time something is sent.
 */
import Icon from '../../lib/icons.jsx';
import { CHANNELS, CAMPAIGN_STATUSES, GOALS } from '../../constants/marketing.js';
import { newestFirst, scheduleState } from '../../domain/marketing.js';

/** Nothing chosen. Shared so "is anything filtered" is one comparison. */
export const NO_FILTERS = {
  term: '', status: '', channel: '', goal: '', audience: '', when: '', sort: 'new'
};

const SORTS = [
  ['new', 'Newest first'],
  ['old', 'Oldest first'],
  ['due', 'Due soonest'],
  ['sent', 'Most sent'],
  ['replied', 'Most replied']
];

const WHENS = [
  ['overdue', 'Overdue'],
  ['soon', 'Due in the next day'],
  ['later', 'Scheduled later'],
  ['done', 'Already sent']
];

export const isFiltered = f =>
  Object.keys(NO_FILTERS).some(k => (f[k] || NO_FILTERS[k]) !== NO_FILTERS[k]);

/**
 * The list, narrowed and ordered.
 *
 * `stats` is passed in rather than computed here: working out what a campaign
 * sent means resolving its audience, and doing that inside a sort comparator
 * would do it a few hundred times for a list of twenty.
 */
export function applyFilters(campaigns, f, statsOf) {
  const q = String(f.term || '').trim().toLowerCase();

  const kept = campaigns.filter(c => {
    if (f.status && c.status !== f.status) return false;
    if (f.channel && c.channel !== f.channel) return false;
    if (f.goal && c.goal !== f.goal) return false;
    if (f.audience && c.audience !== f.audience) return false;

    if (f.when) {
      const due = scheduleState(c);
      const sent = c.status === 'Sent';

      if (f.when === 'done' && !sent) return false;
      if (f.when === 'overdue' && (!due || due.tone !== 'over')) return false;
      if (f.when === 'soon' && (!due || due.tone !== 'due')) return false;
      if (f.when === 'later' && (!due || due.tone !== 'ahead')) return false;
    }

    if (!q) return true;
    return [c.name, c.subject, c.goal].some(v => String(v || '').toLowerCase().includes(q));
  });

  if (f.sort === 'old') return newestFirst(kept).slice().reverse();
  if (f.sort === 'sent') return kept.slice().sort((a, b) => statsOf(b).sent - statsOf(a).sent);
  if (f.sort === 'replied') {
    return kept.slice().sort((a, b) => statsOf(b).replied - statsOf(a).replied);
  }

  if (f.sort === 'due') {
    /* Soonest first, and anything with no date at all after everything that
       has one — an undated campaign is not "due in the year 1970". */
    return kept.slice().sort((a, b) => {
      const x = scheduleState(a);
      const y = scheduleState(b);
      if (!x && !y) return 0;
      if (!x) return 1;
      if (!y) return -1;
      return x.days - y.days;
    });
  }

  return newestFirst(kept);
}

export default function CampaignFilters({
  all, shown, value, onChange, audiences = [], channels = true
}) {
  const f = { ...NO_FILTERS, ...value };
  const set = (k, v) => onChange({ ...f, [k]: v });

  const counts = all.reduce((t, c) => ({ ...t, [c.status]: (t[c.status] || 0) + 1 }), {});

  return (
    <div className="cfbar">
      <div className="cfsearch">
        <Icon name="search" />
        <input
          value={f.term}
          placeholder="Search by name, subject or goal…"
          onChange={e => set('term', e.target.value)}
        />
        {f.term ? (
          <button type="button" aria-label="Clear the search" onClick={() => set('term', '')}>
            <Icon name="x" />
          </button>
        ) : null}
      </div>

      <div className="cfchips">
        <button
          type="button"
          className={`cvchip${f.status ? '' : ' on'}`}
          onClick={() => set('status', '')}
        >
          All<em>{all.length}</em>
        </button>

        {CAMPAIGN_STATUSES.map(x => (
          <button
            type="button"
            key={x}
            className={`cvchip${f.status === x ? ' on' : ''}`}
            disabled={!counts[x]}
            onClick={() => set('status', f.status === x ? '' : x)}
          >
            {x}<em>{counts[x] || 0}</em>
          </button>
        ))}
      </div>

      {channels ? (
        <select
          className="cfchan"
          value={f.channel}
          onChange={e => set('channel', e.target.value)}
          aria-label="Channel"
        >
          <option value="">Every channel</option>
          {CHANNELS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      ) : null}

      <select
        className="cfchan"
        value={f.goal}
        onChange={e => set('goal', e.target.value)}
        aria-label="Goal"
      >
        <option value="">Any goal</option>
        {GOALS.map(([label]) => <option key={label} value={label}>{label}</option>)}
      </select>

      {audiences.length ? (
        <select
          className="cfchan"
          value={f.audience}
          onChange={e => set('audience', e.target.value)}
          aria-label="Audience"
        >
          <option value="">Any audience</option>
          {audiences.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      ) : null}

      <select
        className="cfchan"
        value={f.when}
        onChange={e => set('when', e.target.value)}
        aria-label="When"
      >
        <option value="">Any time</option>
        {WHENS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>

      <select
        className="cfchan sortsel"
        value={f.sort}
        onChange={e => set('sort', e.target.value)}
        aria-label="Order"
      >
        {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>

      {isFiltered(f) ? (
        <button type="button" className="btn sm" onClick={() => onChange({ ...NO_FILTERS })}>
          Clear
        </button>
      ) : null}

      <span className="cfcount">{shown} of {all.length}</span>
    </div>
  );
}
